/**
 * booking.js — Flusso di prenotazione: raccolta dati, check disponibilità,
 *              calcolo preventivo e costruzione riepilogo.
 * Dipende da: env.js, state.js, validators.js, api.js, utils.js, ui.js
 */

// ── Step 1 — raccolta dati viaggio ─────────────────────────────

function collectStep1() {
    const tipoTratta = document.querySelector('input[name="tipoTratta"]:checked')?.value || 'Andata';

    const typeMap = {
        'Andata':          'andata',
        'Andata & Ritorno':'andata_ritorno_attesa',
        'Multitratta':     '',
    };
    let resType = typeMap[tipoTratta] ?? 'andata';

    if (tipoTratta === 'Andata & Ritorno') {
        const modalita = document.querySelector('input[name="modalitaRitorno"]:checked')?.value || 'attesa';
        resType = {
            attesa:      'andata_ritorno_attesa',
            richiamata:  'andata_ritorno_richiamata',
            prestabilito:'andata_ritorno_orari_def',
        }[modalita] ?? 'andata_ritorno_attesa';
    }

    const rawDate       = document.getElementById('dataPartenza')?.value || '';
    const rawReturnDate = document.getElementById('dataRitorno')?.value  || '';
    const startDate     = normalizeDate(rawDate);

    BookingState.reservation_type = resType;
    BookingState.start_date       = startDate;
    BookingState.return_date      = normalizeDate(rawReturnDate) || startDate;
    BookingState.start_time       = document.getElementById('oraPartenza')?.value || '';
    BookingState.return_time      = document.getElementById('oraRitorno')?.value  || '';

    const tipoPart = document.getElementById('tipologiaPartenza');
    const tipoArr  = document.getElementById('tipologiaArrivo');
    if (tipoPart) BookingState.start_address_opts.type = tipoPart.value || '';
    if (tipoArr)  BookingState.end_address_opts.type   = tipoArr.value  || '';

    // Fallback testo libero se l'autocomplete non ha ancora restituito il luogo
    if (!BookingState.start_address)
        BookingState.start_address = document.getElementById('daInput')?.value || '';
    if (!BookingState.end_address)
        BookingState.end_address = document.getElementById('aInput')?.value || '';
}

// ── Step 2 — raccolta passeggeri ed esigenze speciali ───────────

function collectStep2() {
    BookingState.seat       = parseInt(document.getElementById('carrozzinaInput')?.value)    || 0;
    BookingState.seat_guest = parseInt(document.getElementById('accompagnatoriInput')?.value) || 0;

    const ascensore = document.querySelector('input[name="ascensore"]:checked')?.value    || 'No';
    const jolly     = document.querySelector('input[name="jolly"]:checked')?.value        || 'No';
    const sedia     = document.querySelector('input[name="sediaFornita"]:checked')?.value || 'No';
    const note      = document.getElementById('noteAggiuntive')?.value || '';

    const opts = {
        ascensore: ascensore.toLowerCase() === 'si' ? 'si' : '',
        jolly:     jolly.toLowerCase()     === 'si' ? 'si' : '',
        sedia:     sedia.toLowerCase()     === 'si' ? 'si' : '',
        note,
    };
    Object.assign(BookingState.start_address_opts, opts);
    Object.assign(BookingState.end_address_opts,   opts);
}

// ── Check disponibilità ─────────────────────────────────────────

// Debounce: scatta dopo ogni cambio di data/ora/tipo tratta
function triggerAvailabilityCheck() {
    readyForTrack = false;
    clearTimeout(availabilityTimer);
    availabilityTimer = setTimeout(() => {
        collectStep1();
        if (Validators.canCheckAvailability()) checkAvailability();
    }, 700);
}

// Chiamata API: outreach.getAvailableReservation
async function checkAvailability() {
    const resultEl = document.getElementById('availabilityResult');
    if (resultEl) {
        resultEl.className  = 'avail-result mt-3 mb-1 text-muted';
        resultEl.innerHTML  = '<span class="spinner-border spinner-border-sm me-2 align-middle" role="status"></span>Verifica disponibilità in corso...';
        resultEl.classList.remove('d-none');
    }

    const result = await api.execItem('outreach.getAvailableReservation', BookingState, 'cron:1');

    if (result?.result?.success) {
        const avail = result.result.is_availble;
        readyForTrack = avail > 0;
        if (resultEl) {
            if (avail > 0) {
                resultEl.className   = 'avail-result mt-3 mb-1 text-success fw-medium';
                resultEl.textContent = '✓ Van disponibili per le date selezionate';
            } else {
                resultEl.className   = 'avail-result mt-3 mb-1 text-danger';
                resultEl.textContent = 'Nessun van disponibile in queste date. Modifica le date e riprova.';
            }
        }
    } else {
        readyForTrack = false;
        if (resultEl) {
            resultEl.className   = 'avail-result mt-3 mb-1 text-danger';
            resultEl.textContent = 'Errore nel controllo disponibilità. Riprova.';
        }
    }
}

// ── Check regione indirizzo (solo Lazio) ────────────────────────

function checkAddressRegion(place, inputEl, type) {
    const components = place.address_components || [];
    const region = components.find(c => c.types.includes('administrative_area_level_1'));
    const inLazio = region
        ? region.long_name.toLowerCase().includes('lazio')
        : (place.formatted_address || '').toLowerCase().includes('lazio');
    if (inLazio) return true;
    // Indirizzo fuori regione: pulisce il campo e mostra errore
    if (type === 'start') {
        BookingState.start_address = '';
        BookingState.start_address_lat = '';
        BookingState.start_address_lng = '';
    } else {
        BookingState.end_address = '';
        BookingState.end_address_lat = '';
        BookingState.end_address_lng = '';
    }
    if (inputEl) inputEl.value = '';
    readyForTrack = false;
    alert('Non puoi selezionare un indirizzo fuori dalla regione Lazio.\nPer trasferimenti fuori regione contatta il servizio clienti.');
    return false;
}

// ── Fetch percorso + mappa statica ─────────────────────────────

async function fetchRouteMap() {
    const slat = BookingState.start_address_lat;
    const slng = BookingState.start_address_lng;
    const elat = BookingState.end_address_lat;
    const elng = BookingState.end_address_lng;
    if (!slat || !slng || !elat || !elng) return;

    const dirRes = await api.execItem('maps.getDirectionsService', {
        alternatives:   'false',
        origin:         `${slat},${slng}`,
        destination:    `${elat},${elng}`,
        mode:           'driving',
        traffic_model:  'best_guess',
        departure_time: 'now',
    }, 'cron:1');

    if (!dirRes?.result?.success) return;
    BookingState.directionsData = dirRes.result.directions;

    const mapRes = await api.execItem('maps.getMapsStatic', {
        directions: dirRes.result.directions,
        size:       '600x400',
    }, 'cron:1');

    const url = typeof mapRes?.result === 'string'
        ? mapRes.result
        : (mapRes?.result?.url || '');
    BookingState.mapImageUrl = url;

    // Aggiorna il box mappa se lo step 3 è già visibile
    const mapBox = document.getElementById('mapBoxStep3');
    if (mapBox && url) {
        mapBox.style.backgroundImage = `url('${url}')`;
        mapBox.classList.remove('d-none');
    }
}

// ── Step 3 — caricamento riepilogo e preventivo ─────────────────

function isTuristicByType() {
    const isStation = t => (t || '').toLowerCase().includes('stazioni');
    return isStation(BookingState.start_address_opts.type)
        || isStation(BookingState.end_address_opts.type);
}

async function loadSummaryStep() {
    injectStep3Fields();
    buildSummaryHTML();
    track('booking_step3_view', {
        reservation_type: BookingState.reservation_type,
        start_date:       BookingState.start_date,
        start_address:    BookingState.start_address,
        end_address:      BookingState.end_address,
        seat:             BookingState.seat,
        seat_guest:       BookingState.seat_guest,
    });

    if (isTuristicByType()) injectTuristicFields();

    // Genera la mappa se non è ancora disponibile (es. indirizzi da hash params)
    if (!BookingState.mapImageUrl
        && BookingState.start_address_lat
        && BookingState.end_address_lat) {
        fetchRouteMap();
    }

    if (!readyForSummary) {
        readyForSummary = true;
        await fetchSummaryPrice();
    }
}

// Chiamata API: outreach.getSummaryReservation
async function fetchSummaryPrice() {
    const priceBox = document.getElementById('priceBox');
    if (priceBox) {
        priceBox.classList.remove('d-none');
        document.getElementById('priceTotal').textContent = 'Elaborazione preventivo in corso...';
        document.getElementById('priceTotal').classList.remove('text-danger');
    }

    const result = await api.execItem('outreach.getSummaryReservation', BookingState, 'cron:1');

    if (result?.result?.success) {
        BookingState.Summary = result.result;
        readyForPayment = true;

        const total    = result.result.total;
        const vans     = result.result.number_of_vans;
        const turistic = result.result?.data?.reservation_price_model?.locationIsTuristic === 2;

        document.getElementById('priceTotal').textContent =
            `TOT € ${total}`;
        document.getElementById('priceVans').textContent =
            `Numero van prenotati: ${vans}`;
        track('booking_price_calculated', {
            reservation_type: BookingState.reservation_type,
            total,
            number_of_vans:   vans,
            start_date:       BookingState.start_date,
        });
        document.getElementById('priceExtra').textContent =
            BookingState.reservation_type === 'andata_ritorno_attesa'
            ? 'Nel prezzo è inclusa 1 ora di attesa. Ogni ora extra ha un costo variabile.'
            : '';

        // Aggiorna testo del bottone "Paga ora" con il prezzo reale
        const btnPagaOra = document.querySelector('#step-2 .btn-avanti-dark:not(#btnConfirmStripe)');
        if (btnPagaOra) btnPagaOra.textContent = `Paga ora — € ${total}`;

        if (turistic) injectTuristicFields();

    } else {
        readyForPayment = false;
        const msg = result?.result?.errorInfo || 'Errore nel calcolo del preventivo.';
        const totalEl = document.getElementById('priceTotal');
        if (totalEl) { totalEl.textContent = msg; totalEl.classList.add('text-danger'); }
    }
}

// ── Costruzione HTML riepilogo viaggio ──────────────────────────

function buildSummaryHTML() {
    const s = BookingState;
    const retRow = s.reservation_type !== 'andata' && s.return_time
        ? `<div class="mt-1"><strong class="fw-semibold">Ritorno:</strong> ${s.return_date} ore ${s.return_time}</div>`
        : '';

    const mapBox = document.getElementById('mapBoxStep3');
    if (mapBox) {
        if (s.mapImageUrl) {
            mapBox.style.backgroundImage = `url('${s.mapImageUrl}')`;
            mapBox.classList.remove('d-none');
        } else {
            mapBox.classList.add('d-none');
        }
    }

    document.getElementById('summaryContent').innerHTML = `
        <h5 class="fw-bold mb-4">Riepilogo: ${reservationTypeLabel(s.reservation_type)}</h5>

        <div class="d-flex align-items-start mb-3">
          <div class="bg-light rounded-circle d-flex align-items-center justify-content-center me-3 text-primary-dark"
               style="width:40px;height:40px;flex-shrink:0;">
            <img src="${APP_IMG_BASE}icon/map-pinpoint-01.svg" width="20" height="20" alt="">
          </div>
          <div>
            <div class="mb-1"><strong class="fw-semibold">Da:</strong> ${s.start_address || '—'}</div>
            <div><strong class="fw-semibold">A:</strong> ${s.end_address || '—'}</div>
          </div>
        </div>

        <div class="d-flex align-items-start mb-3 pt-2 border-top">
          <div class="bg-light rounded-circle d-flex align-items-center justify-content-center me-3 text-primary-dark"
               style="width:40px;height:40px;flex-shrink:0;">
            <img src="${APP_IMG_BASE}icon/user-group.svg" width="20" height="20" alt="">
          </div>
          <div>
            <div class="mb-1">
              <strong class="fw-semibold">${s.seat}</strong> passeggero/i in carrozzina
            </div>
            <div>
              <strong class="fw-semibold">${s.seat_guest}</strong> accompagnatore/i
            </div>
          </div>
        </div>

        <div class="d-flex align-items-start pt-2 border-top">
          <div class="bg-light rounded-circle d-flex align-items-center justify-content-center me-3 text-primary-dark"
               style="width:40px;height:40px;flex-shrink:0;">
            <img src="${APP_IMG_BASE}icon/elements-3.svg" width="20" height="20" alt="">
          </div>
          <div>
            <div class="mb-1">
              <strong class="fw-semibold">Quando:</strong> ${formatDateLabel(s.start_date)}
            </div>
            <div>
              <strong class="fw-semibold">Orario:</strong> ${s.start_time || '—'}
            </div>
            ${retRow}
          </div>
        </div>

        `;
}
